// TODO: Important: Licensed code, research if can be used

/*
 * Copyright 2024 Google LLC. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useContext } from 'react';
import { useState, useEffect, useRef } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';

import { FormItem, FormLabel } from '@/components/ui/form';

import { Input } from '@/components/ui/input';

import { ReadOnlyField } from '@/lib/pages/forms/components';

import { PermissionsContext } from '@/lib/layout/PermissionContext';

interface PlaceAutocompleteProps {
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  orientation: 'horizontal' | 'vertical';
  label: string;
  defaultValue: string | undefined;
  required: boolean;
  asSearchField?: boolean;
  readOnly?: boolean;
  stretch?: boolean;
  wideLabel?: boolean;
  overwriteLabelWidth?: string;
  overwriteInputWidth?: string;
}

function getStreetAddress(place: google.maps.places.PlaceResult): string {
  const line: Array<string> = [];
  place.address_components?.forEach(({ short_name, long_name, types }) => {
    if (types.includes('street_number')) {
      line[0] = long_name;
    }

    if (types.includes('route')) {
      line[1] = long_name;
    }
  });

  return line.filter((pred) => pred != undefined).join(' ');
}

const PlaceAutocomplete = ({
  onPlaceSelect,
  orientation,
  label,
  defaultValue,
  required,
  asSearchField,
  readOnly,
  stretch = false,
  wideLabel,
  overwriteLabelWidth,
  overwriteInputWidth,
}: PlaceAutocompleteProps) => {
  const { editMode, responsive } = useContext(PermissionsContext);
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary('places');

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: [
        'geometry',
        'name',
        'formatted_address',
        'address_components',
        '	adr_address',
        'geometry/location',
      ],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    placeAutocomplete.addListener('place_changed', () => {
      onPlaceSelect(placeAutocomplete.getPlace());

      if (inputRef.current) {
        if (asSearchField) {
          inputRef.current.value = '';
        } else {
          inputRef.current.value = getStreetAddress(
            placeAutocomplete.getPlace()
          );
        }
      }
    });
  }, [onPlaceSelect, placeAutocomplete]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = defaultValue || '';
    }
  }, [defaultValue, editMode]);

  return (
    <div className="autocomplete-container">
      {!editMode || readOnly ? (
        <ReadOnlyField label={label} value={defaultValue} />
      ) : (
        <FormItem
          className={`flex ${orientation === 'horizontal' ? 'items-center gap-4' : 'w-full flex-col'} ${responsive ? 'max-md:w-full max-md:flex-col' : ''}`}
        >
          <FormLabel
            className={`${orientation === 'horizontal' ? `${overwriteLabelWidth ? overwriteLabelWidth : `${stretch ? `${wideLabel ? 'w-2/3' : 'w-1/3'}` : `${wideLabel ? 'w-2/5' : 'w-1/5'}`}`}` : 'mb-2'} ${responsive ? 'max-md:w-full' : ''} font-semibold leading-[1.5rem]`}
          >
            {label}
            {required && <span className="text-red-400">*</span>}
          </FormLabel>
          <div
            className={`${orientation === 'horizontal' ? `${overwriteInputWidth ? overwriteInputWidth : `${stretch ? `${wideLabel ? 'w-1/3' : 'w-2/3'}` : `${wideLabel ? 'w-1/5' : 'w-2/5'}`}`}` : ''} ${responsive ? 'max-md:w-full' : ''} !mt-0`}
          >
            <Input
              className="border"
              placeholder="Enter location"
              ref={inputRef}
              // required={required} // uncomment to enable FE validation
            />
          </div>
        </FormItem>
      )}
    </div>
  );
};

export const GoogleInput = ({
  onSelectPlace,
  label,
  defaultValue,
  orientation = 'horizontal',
  required = false,
  asSearchField,
  readOnly = false,
  stretch = false,
  wideLabel,
  overwriteLabelWidth,
  overwriteInputWidth,
}: {
  onSelectPlace: (e: google.maps.places.PlaceResult) => void;
  label: string;
  defaultValue: string | undefined;
  placeholder: string;
  orientation: 'horizontal' | 'vertical';
  required?: boolean;
  asSearchField?: boolean;
  readOnly?: boolean;
  stretch?: boolean;
  wideLabel?: boolean;
  overwriteLabelWidth?: string;
  overwriteInputWidth?: string;
}) => {
  const handleSelectPlace = (e: google.maps.places.PlaceResult | null) => {
    if (e) {
      onSelectPlace(e);
    }
  };

  return (
    <APIProvider
      apiKey={import.meta.env.VITE_API_KEY}
      solutionChannel="GMP_devsite_samples_v3_rgmautocomplete"
    >
      {/* <Map
        mapId={'bf51a910020fa25a'}
        defaultZoom={3}
        defaultCenter={{ lat: 22.54992, lng: 0 }}
        gestureHandling={'greedy'}
        disableDefaultUI={true}
      >
        <AdvancedMarker ref={markerRef} position={null} />
      </Map> */}
      {/* <MapControl position={ControlPosition.TOP}> */}
      <div className="autocomplete-control flex w-full flex-col space-y-2">
        <PlaceAutocomplete
          onPlaceSelect={handleSelectPlace}
          orientation={orientation}
          label={label}
          defaultValue={defaultValue}
          required={required}
          asSearchField={asSearchField}
          readOnly={readOnly}
          stretch={stretch}
          wideLabel={wideLabel}
          overwriteLabelWidth={overwriteLabelWidth}
          overwriteInputWidth={overwriteInputWidth}
        />
      </div>
      {/* </MapControl> */}
      {/* <MapHandler place={selectedPlace} marker={marker} /> */}
    </APIProvider>
  );
};

export default GoogleInput;
